/**
 * "Re-run TRDAAS" beside the L1 Ingest box (Ben, 2026-10-22): run a game's
 * CV tracking again on the current worker, as a NEW job — for a game
 * tracked weeks ago on an older format (LOU v DEN, game 170). Platform
 * admins only; it launches a paid GPU fleet, so it confirms first and
 * takes a reason.
 *
 * The dialog shows what the server will do (GET rerun-tracking): the video
 * it uses, and whether an earlier approved calibration is reused (straight
 * to the full run) or it calibrates first (it then waits in calibration
 * review). The game keeps its current data until the new result lands; then
 * it moves to the new job, the standard completion runs, and "Ingest
 * tracking" offers the new file for the layer stack (services/tracking_rerun).
 */
import { useState } from 'react'

import { ApiError, api } from '../lib/api'
import { useActionLog } from './actionLog'

type Preview = {
  game_code: string
  video_key: string
  video_size_mb: number
  calibrate_first: boolean
  calibration_from_job: string | null
  current_job_id: string | null
}
type Started = { job_id: string; calibrate_first: boolean; status: string }

const msg = (e: unknown) => (e instanceof ApiError ? e.message : String(e))
const short = (id: string | null) => (id ? id.slice(0, 8) : 'none')

export function RerunTrdaas({ gameId }: { gameId: number }) {
  const { log } = useActionLog()
  const [open, setOpen] = useState(false)
  const [preview, setPreview] = useState<Preview | null>(null)
  const [refused, setRefused] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [started, setStarted] = useState<Started | null>(null)

  const openDialog = async () => {
    setOpen(true)
    setPreview(null)
    setRefused(null)
    setStarted(null)
    setReason('')
    try {
      setPreview(await api<Preview>(`/api/v1/ops/games/${gameId}/rerun-tracking`))
    } catch (e: unknown) {
      setRefused(msg(e))
    }
  }

  const confirm = async () => {
    const why = reason.trim()
    if (!why || busy) return
    setBusy(true)
    log('start', `Re-run TRDAAS: starting (${why})`)
    try {
      const r = await api<Started>(`/api/v1/ops/games/${gameId}/rerun-tracking`,
        { method: 'POST', body: JSON.stringify({ reason: why }) })
      setStarted(r)
      log('done', `Re-run TRDAAS: job ${r.job_id} ${r.status}`
        + (r.calibrate_first ? ' — calibrating; approve it in calibration review'
          : ' — full run on the reused calibration'))
    } catch (e: unknown) {
      setRefused(msg(e))
      log('fail', `Re-run TRDAAS: FAILED — ${msg(e)}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <button type="button" className="adm-btn ls-rerun-trdaas"
              title="Run this game's CV tracking again on the current worker, as a new job"
              onClick={() => void openDialog()}>
        Re-run TRDAAS
      </button>
      {open && (
        <div className="ls-modal-backdrop" role="presentation"
             onClick={() => !busy && setOpen(false)}>
          <div className="ls-modal" role="dialog" aria-modal="true"
               aria-labelledby="rerun-trdaas-title"
               onClick={(e) => e.stopPropagation()}>
            <h3 id="rerun-trdaas-title">Re-run TRDAAS?</h3>
            {started ? (
              <>
                <p>Started job <code>{started.job_id}</code> ({started.status}).</p>
                {started.calibrate_first ? (
                  <p>It is calibrating. When it is ready, approve it in{' '}
                    <a href={`/games/${started.job_id}/calibration`}>calibration review</a>;
                    {' '}the full run starts on approval.</p>
                ) : (
                  <p>The full run is under way on the reused calibration.</p>
                )}
                <p className="muted">Until it finishes the game keeps its current data. When the
                  result lands the game moves to the new job and its standard import runs; Ingest
                  tracking then offers the new file for the layer stack, to build on.</p>
                <div className="ls-modal__actions">
                  <button type="button" className="adm-btn" onClick={() => setOpen(false)}>Close</button>
                </div>
              </>
            ) : refused ? (
              <>
                <p className="error">Cannot re-run: {refused}</p>
                <div className="ls-modal__actions">
                  <button type="button" className="adm-btn" onClick={() => setOpen(false)}>Close</button>
                </div>
              </>
            ) : !preview ? (
              <p className="muted">Checking what a re-run would use…</p>
            ) : (
              <>
                <p>Runs <strong>{preview.game_code}</strong>&apos;s CV tracking again on the
                  current worker, as a <strong>new job</strong> with its own folder.
                  Nothing the game reads today is overwritten.</p>
                <dl className="ls-modal__facts">
                  <dt>Video</dt>
                  <dd><code>{preview.video_key}</code> ({preview.video_size_mb.toLocaleString()} MB)</dd>
                  <dt>Calibration</dt>
                  <dd>{preview.calibrate_first
                    ? 'None approved to reuse: it calibrates first, then waits for a person to approve it in calibration review.'
                    : `Reuses job ${short(preview.calibration_from_job)}'s approved calibration: the full run starts now.`}</dd>
                  <dt>Current job</dt>
                  <dd><code>{short(preview.current_job_id)}</code> — kept until the new result lands</dd>
                </dl>
                <p className="ls-modal__warn">This launches a paid GPU fleet.</p>
                <label className="ls-modal__field">
                  <span>Why (required)</span>
                  <textarea className="adm-input" rows={2} value={reason} autoFocus
                            aria-label="Reason for the re-run"
                            placeholder="e.g. tracked on the older format; needs the current worker"
                            onChange={(e) => setReason(e.target.value)} />
                </label>
                <div className="ls-modal__actions">
                  <button type="button" className="adm-btn" disabled={busy}
                          onClick={() => setOpen(false)}>Cancel</button>
                  <button type="button" className="adm-btn adm-btn--danger"
                          disabled={busy || !reason.trim()}
                          onClick={() => void confirm()}>
                    {busy ? 'Starting…' : 'Confirm re-run'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  )
}
